import React from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  removeFromCart,
  incrementQty,
  decrementQty,
} from "../redux/cartSlice";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaShoppingCart, FaTrashAlt } from "react-icons/fa";
import HlcFooter from "../components/HlcFooter";
import Navbar from "../components/Navbar";
import { getProductImage } from "../utils/getProductImage";

const parsePrice = (price) => {
  if (typeof price === "number") return price;
  if (typeof price === "string") {
    const n = parseFloat(price.replace(/[^\d.-]/g, ""));
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

const CartScreen = () => {
  const cartItems = useSelector((state) => state.cart.items);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const totalPrice = cartItems.reduce((acc, item) => {
    const p = parsePrice(item.discountedPrice || item.price);
    return acc + p * (item.quantity || 1);
  }, 0);

  return (
    <>
      <Navbar />

      <div className="min-h-screen mt-16" style={{ background: "#fafafa" }}>
        <div
          className="relative py-8 md:py-12 text-center"
          style={{
            background:
              "linear-gradient(rgba(155,31,108,0.9), rgba(155,31,108,0.9))",
            color: "#fff",
          }}
        >
          <div className="max-w-7xl mx-auto px-4">
            <h1 className="text-2xl md:text-3xl font-medium">Shopping Cart</h1>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8">
          {cartItems.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center justify-center py-20 text-center"
            >
              <FaShoppingCart className="text-6xl text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold mb-2 text-gray-900">
                Your cart is empty
              </h3>
              <p className="text-gray-600 mb-6">
                Start shopping to add items to your cart
              </p>
              <button
                onClick={() => navigate("/")}
                className="bg-[#9b1f6c] hover:bg-[#7a1650] text-white font-medium px-8 py-3 rounded-md transition-all duration-300"
              >
                Continue Shopping
              </button>
            </motion.div>
          ) : (
            <form className="relative overflow-hidden">
              {/* Header Row */}
              <div className="hidden md:block mb-4">
                <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-white rounded-t-lg font-medium text-gray-700 border-b border-gray-200">
                  <div className="col-span-5">Product</div>
                  <div className="col-span-3 text-center">Price</div>
                  <div className="col-span-2 text-center">Quantity</div>
                  <div className="col-span-2 text-center md:text-right">
                    Total
                  </div>
                </div>
              </div>

              {/* Cart Items */}
              <div className="space-y-4 mb-8">
                <AnimatePresence>
                  {cartItems.map((item, index) => {
                    const itemImage = getProductImage(item);

                    return (
                      <motion.div
                        key={`${item.id}-${item.selectedWatt || ""}-${item.selectedColor || ""}-${item.selectedPin || ""}-${index}`}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300"
                      >
                        <div className="grid grid-cols-12 gap-4 p-4 items-center">
                          {/* Product Info */}
                          <div className="col-span-12 md:col-span-5">
                            <div className="flex items-center gap-4">
                              <div className="relative w-20 h-20 md:w-24 md:h-24 flex-shrink-0 bg-gray-100 rounded-md overflow-hidden border border-gray-200">
                                {itemImage ? (
                                  <img
                                    src={itemImage}
                                    alt={item.name}
                                    className="w-full h-full object-contain p-2"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">
                                    No Image
                                  </div>
                                )}
                              </div>

                              <div className="flex-1 min-w-0">
                                <h3 className="font-medium text-gray-900 text-sm md:text-base mb-1 line-clamp-2">
                                  {item.name}
                                </h3>

                                {item.selectedColor && (
                                  <p className="text-xs text-gray-600">
                                    Color: <strong>{item.selectedColor}</strong>
                                  </p>
                                )}

                                {item.selectedPin && (
                                  <p className="text-xs text-gray-600">
                                    Type: <strong>{item.selectedPin}</strong>
                                  </p>
                                )}

                                {item.selectedWatt && (
                                  <p className="text-xs text-gray-600">
                                    Watt: <strong>{item.selectedWatt}</strong>
                                  </p>
                                )}

                                {item.selectedSize && (
                                  <p className="text-xs text-gray-600">
                                    Size: <strong>{item.selectedSize}</strong>
                                  </p>
                                )}

                                <div className="flex gap-3 mt-2">
                                  <button
                                    type="button"
                                    onClick={() => dispatch(removeFromCart(item.id))}
                                    className="text-[#9b1f6c] hover:text-[#7a1650] text-xs flex items-center gap-1"
                                  >
                                    <FaTrashAlt className="w-3 h-3" />
                                    <span>Remove</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Price */}
                          <div className="col-span-12 md:col-span-3 text-left md:text-center">
                            <div className="md:hidden text-xs text-gray-600 mb-1">
                              Price:
                            </div>
                            <div className="text-gray-900 font-medium">
                              Rs.{" "}
                              {parsePrice(
                                item.discountedPrice || item.price
                              ).toLocaleString()}
                            </div>
                          </div>

                          {/* Quantity */}
                          <div className="col-span-12 md:col-span-2 text-left md:text-center">
                            <div className="md:hidden text-xs text-gray-600 mb-1">
                              Quantity:
                            </div>
                            <div className="inline-flex items-center border border-gray-300 rounded-md overflow-hidden">
                              <button
                                type="button"
                                onClick={() => dispatch(decrementQty(item.id))}
                                className="px-3 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 transition-colors"
                              >
                                −
                              </button>

                              <input
                                type="number"
                                value={item.quantity || 1}
                                readOnly
                                className="w-12 text-center border-0 py-2 bg-white outline-none"
                              />

                              <button
                                type="button"
                                onClick={() => dispatch(incrementQty(item.id))}
                                className="px-3 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 transition-colors"
                              >
                                +
                              </button>
                            </div>
                          </div>

                          {/* Total */}
                          <div className="col-span-12 md:col-span-2 text-left md:text-right">
                            <div className="md:hidden text-xs text-gray-600 mb-1">
                              Total:
                            </div>
                            <span className="text-gray-900 font-semibold">
                              Rs.{" "}
                              {(
                                parsePrice(item.discountedPrice || item.price) *
                                (item.quantity || 1)
                              ).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>

              {/* Bottom Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Add Order Note
                    </label>
                    <textarea
                      placeholder="How can we help you?"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#9b1f6c] focus:border-transparent resize-none"
                      rows="4"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Coupon:
                    </label>
                    <p className="text-xs text-gray-600 mb-2">
                      Coupon code will work on checkout page
                    </p>
                    <input
                      type="text"
                      placeholder="Coupon code"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#9b1f6c] focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Right */}
                <div className="flex flex-col justify-end">
                  <div className="bg-white rounded-lg p-6 space-y-4 border border-gray-200">
                    <div className="flex items-center justify-between text-lg">
                      <strong className="text-gray-900">Subtotal:</strong>
                      <div className="text-right">
                        <div className="text-xl font-bold text-[#9b1f6c]">
                          Rs.{" "}
                          {totalPrice.toLocaleString(undefined, {
                            maximumFractionDigits: 2,
                          })}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => navigate("/checkout")}
                      type="button"
                      className="w-full bg-[#9b1f6c] hover:bg-[#7a1650] text-white font-medium py-4 rounded-md transition-all duration-300 text-base"
                    >
                      Check Out
                    </button>
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>

      <HlcFooter />
    </>
  );
};

export default CartScreen;